import { ACTIVE_ORDER_STATUSES, PRODUCT_COLORS, SAVED_COST_PER_INVALID_ADDRESS } from './constants';

const EMPTY_CONTACTS_OVERVIEW = {
  total: 0,
  deliverable: 0,
  deliverableRate: 0,
  imported: 0,
  suppressed: 0,
  undeliverable: 0,
  onActiveMailings: 0,
  lastRefreshedAt: null,
  hasContacts: false,
  isDashboardCardsDisabled: false,
  isSampleData: false,
  mostUsedLists: [],
};

const EMPTY_ENGAGEMENT_OVERVIEW = {
  trackingEnabled: false,
  trackedDelivered: 0,
  uniqueResponders: 0,
  totalResponses: 0,
  responseRate: 0,
  phoneCalls: 0,
  qrScans: 0,
  purlVisits: 0,
  lastRefreshedAt: null,
  responseChannels: [],
  performanceByProduct: [],
  trackingAssets: [],
  recentResponses: [],
  accuracy: {},
  isDashboardCardsDisabled: false,
  isSampleData: false,
};

const numberFormatter = new Intl.NumberFormat('en-US');
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

export const formatNumber = (value) => numberFormatter.format(Number(value) || 0);

export const formatCurrency = (value) => currencyFormatter.format(Number(value) || 0);

export const formatPercent = (value, options = {}) => {
  const digits = options.maximumFractionDigits ?? 1;
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) return '0%';

  return `${numericValue.toFixed(digits)}%`;
};

export const getReturnRateLevel = (rate) => {
  if (rate < 5) return 'Low';
  if (rate < 10) return 'Normal';
  return 'High';
};

export const formatAverageValue = (value) => {
  const days = Number(value);
  return Number.isFinite(days) ? days.toFixed(1) : '-';
};

export const formatAverageDays = (value) => {
  const days = Number(value);
  if (!Number.isFinite(days)) return '-';

  return `${days.toFixed(1)} ${days === 1 ? 'day' : 'days'}`;
};

export const formatDateForDuration = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const formatCustomDateLabel = (startDate, endDate) => {
  const monthDayFormatter = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  });
  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();
  const sameYear = startYear === endYear;
  const sameMonth = sameYear && startDate.getMonth() === endDate.getMonth();

  if (sameMonth) {
    return `${monthDayFormatter.format(startDate)}-${endDate.getDate()}, ${endYear}`;
  }

  if (sameYear) {
    return `${monthDayFormatter.format(startDate)}-${monthDayFormatter.format(endDate)}, ${endYear}`;
  }

  return `${monthDayFormatter.format(startDate)}, ${String(startYear).slice(-2)}-${monthDayFormatter.format(endDate)}, ${String(endYear).slice(-2)}`;
};

export const humanize = (value = '') =>
  String(value)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

export const getStatusTone = (status = '') => {
  const normalized = status.toLowerCase();
  if (normalized.includes('scheduled')) return 'scheduled';
  if (normalized.includes('deliver') || normalized.includes('complete')) return 'success';
  if (normalized.includes('mail')) return 'mailed';
  if (normalized.includes('transit')) return 'info';
  if (
    normalized.includes('production') ||
    normalized.includes('process') ||
    normalized.includes('pending')
  )
    return 'production';
  if (normalized.includes('return')) return 'danger';
  if (normalized.includes('cancel')) return 'warning';
  return 'neutral';
};

export const getIsGettingStartedHidden = (storageKey) => {
  try {
    return window.localStorage.getItem(storageKey) === 'true';
  } catch {
    return false;
  }
};

export const getMailSummaryViewModel = (mailData = {}) => {
  const delivered = Number(mailData?.delivered) || 0;
  const inTransit = Number(mailData?.inTransit) || 0;
  const returned = Number(mailData?.returnedToSender) || 0;
  const mailed = Number(mailData?.mailed) || 0;
  const mailPiecesSent = Number(mailData?.mailPiecesSent) || 0;
  const mailPiecesOrdered = Number(mailData?.mailPiecesOrdered) || mailPiecesSent;
  const removedBeforeDelivery = Number(mailData?.removedBeforeDelivery) || 0;
  const statusTotal = delivered + inTransit + mailed + returned;
  const progressDenominator = Math.max(mailPiecesSent, statusTotal, 1);
  const returnRate = mailPiecesSent ? (returned / mailPiecesSent) * 100 : 0;

  return {
    delivered,
    inTransit,
    returned,
    mailed,
    mailPiecesSent,
    mailPiecesOrdered,
    removedBeforeDelivery,
    deliveredPercent: (delivered / progressDenominator) * 100,
    transitPercent: (inTransit / progressDenominator) * 100,
    returnedPercent: (returned / progressDenominator) * 100,
    mailedPercent: (mailed / progressDenominator) * 100,
    returnRate,
    returnRateLevel: getReturnRateLevel(returnRate),
    estimatedAddressSavings: removedBeforeDelivery * SAVED_COST_PER_INVALID_ADDRESS,
    hasDeliveryData: statusTotal > 0 || mailPiecesSent > 0 || mailPiecesOrdered > 0,
  };
};

export const getProductMixViewModel = (productMixData = {}, mailPiecesSent = 0) => {
  const entries = Object.entries(productMixData || {})
    .map(([name, value]) => ({ name: humanize(name), value: Number(value) || 0 }))
    .filter((item) => item.value > 0)
    .sort((a, b) => b.value - a.value);
  const total = Math.max(
    mailPiecesSent,
    entries.reduce((sum, item) => sum + item.value, 0),
    1
  );
  let cursor = 0;
  const chartStops = entries.map((item, index) => {
    const percentage = total ? (item.value / total) * 100 : 0;
    const stop = `${PRODUCT_COLORS[index % PRODUCT_COLORS.length]} ${cursor}% ${
      cursor + percentage
    }%`;
    cursor += percentage;
    return stop;
  });

  return {
    entries: entries.map((item, index) => ({
      ...item,
      color: PRODUCT_COLORS[index % PRODUCT_COLORS.length],
      percentage: total ? (item.value / total) * 100 : 0,
    })),
    background: chartStops.length ? `conic-gradient(${chartStops.join(', ')})` : 'none',
  };
};

export const getLastRefreshedLabel = (lastRefreshedAt) =>
  lastRefreshedAt
    ? new Date(lastRefreshedAt).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-';

export const filterActiveOrders = (orders = []) =>
  (orders || []).filter((order) => ACTIVE_ORDER_STATUSES.has(String(order.status).toLowerCase()));

export const getRowsFromApiPayload = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.rows)) return payload.rows;
  if (Array.isArray(payload?.data?.rows)) return payload.data.rows;
  return [];
};

const getRelativeDateLabel = (value) => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const minuteMs = 60 * 1000;
  const hourMs = 60 * minuteMs;
  const dayMs = 24 * hourMs;

  if (diffMs < minuteMs) return 'Just now';
  if (diffMs < hourMs) {
    const minutes = Math.max(1, Math.round(diffMs / minuteMs));
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  }
  if (diffMs < dayMs) {
    const hours = Math.max(1, Math.round(diffMs / hourMs));
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  }
  if (diffMs < 2 * dayMs) return 'Yesterday';
  if (diffMs < 7 * dayMs) {
    const days = Math.round(diffMs / dayMs);
    return `${days} days ago`;
  }

  return date.toLocaleDateString([], {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
};

const getShortDateLabel = (value) => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleDateString([], {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
  });
};

const getCountByAddressStatus = (contactsByMailStatus = [], statusMatchers = []) =>
  (contactsByMailStatus || []).reduce((sum, row) => {
    const status = String(
      row.address_status || row.addressStatus || row.status || ''
    ).toLowerCase();
    const count = Number(row.count || row.addressStatus || row.total || 0) || 0;
    const isMatch = statusMatchers.some((matcher) => status.includes(matcher));

    return isMatch ? sum + count : sum;
  }, 0);

const normalizeMostUsedLists = (mostUsedLists) => {
  if (!Array.isArray(mostUsedLists)) return [];

  return mostUsedLists.map((list, index) => ({
    id: list.id ?? `list-${index}`,
    name: list.name || list.title || 'Untitled list',
    source: list.source || 'Saved list',
    contacts: Number(list.contacts || list.totalContacts) || 0,
    deliverableRate: Number(list.deliverableRate) || 0,
    timesMailed: Number(list.timesMailed) || 0,
    lastMailed: getShortDateLabel(list.lastMailed),
  }));
};

export const normalizeDashboardContacts = ({ contactsData, hasContacts = true } = {}) => {
  const hasServerContacts = contactsData != null;
  const total = hasServerContacts ? Number(contactsData?.totalContacts) || 0 : 0;
  const suppressed = hasServerContacts ? Number(contactsData?.doNotContactContacts) || 0 : 0;
  const hasUndeliverableContacts = contactsData?.undeliverableContacts != null;
  const hasDeliverableContacts = contactsData?.deliverableContacts != null;
  const undeliverable =
    hasUndeliverableContacts
      ? Number(contactsData.undeliverableContacts) || 0
      : getCountByAddressStatus(contactsData?.contactsByMailStatus, [
          'undeliverable',
          'invalid',
          'bad',
          'vacant',
          'failed',
        ]) || 0;
  const deliverable =
    hasDeliverableContacts
      ? Number(contactsData.deliverableContacts) || 0
      : getCountByAddressStatus(contactsData?.contactsByMailStatus, [
          'deliverable',
          'valid',
          'verified',
        ]) || (hasServerContacts ? Math.max(total - undeliverable - suppressed, 0) : 0);
  const deliverableRate = total ? (deliverable / total) * 100 : 0;

  return {
    ...EMPTY_CONTACTS_OVERVIEW,
    total,
    deliverable,
    deliverableRate,
    suppressed,
    undeliverable,
    onActiveMailings: Number(contactsData?.onActiveMailings) || 0,
    lastRefreshedAt: contactsData?.lastRefreshedAt || null,
    hasContacts: total > 0 || (!hasServerContacts && hasContacts),
    isDashboardCardsDisabled: contactsData?.isDashboardCardsDisabled === true,
    isSampleData: contactsData?.isSampleData === true,
    mostUsedLists: normalizeMostUsedLists(contactsData?.mostUsedLists),
  };
};

const CHANNEL_COLORS = {
  phone: '#3E7BC4',
  qr: '#ED5C2F',
  purl: '#5F2FED',
};

const normalizeEngagementChannel = (channel = {}) => ({
  id: channel.id,
  label: channel.label || humanize(channel.id),
  value: Number(channel.value) || 0,
  percent: Number(channel.percent) || 0,
  color: channel.color || CHANNEL_COLORS[channel.id] || '#8C8C8C',
});

const clampPercent = (value) => Math.min(Math.max(Number(value) || 0, 0), 100);

const getTrackingAssetId = (row = {}) => {
  if (row.trackingAssetId !== undefined && row.trackingAssetId !== null) {
    return row.trackingAssetId;
  }

  const idMatch = String(row.id || '').match(/^(?:phone|qr|purl)-(.+)$/i);
  return idMatch?.[1] || null;
};

const normalizeTrackingPerformanceRow = (row = {}) => ({
  id: row.id,
  trackingAssetId: getTrackingAssetId(row),
  asset: row.trackingAsset || '-',
  campaign: row.campaign || row.urlType || '',
  type: row.type || '-',
  mailDelivered: Number(row.mailDelivered) || 0,
  responses: Number(row.responses) || 0,
  responseRate: Number(row.responseRate) || 0,
  lastTrackedAt: row.lastTracked || null,
  lastTracked: getRelativeDateLabel(row.lastTracked),
  lastMailedAt: row.lastMailed || null,
  lastMailed: getRelativeDateLabel(row.lastMailed),
});

const normalizeRecentResponseRow = (row = {}) => ({
  id: row.id,
  contact: row.contact || 'Unknown contact',
  order: row.order || 'Unknown order',
  channel: row.channel || '-',
  sourceType: row.source?.type || null,
  whenAt: row.when || null,
  when: getRelativeDateLabel(row.when),
});

export const normalizeDashboardEngagement = ({ engagementData } = {}) => {
  const stats = engagementData?.stats;
  const trackingRows = getRowsFromApiPayload(engagementData?.trackingPerformance);
  const recentRows = getRowsFromApiPayload(engagementData?.recentResponses);
  const hasEngagementData = stats != null;

  return {
    ...EMPTY_ENGAGEMENT_OVERVIEW,
    trackingEnabled: hasEngagementData ? stats.trackingEnabled === true : false,
    trackedDelivered: Number(stats?.trackedDelivered) || 0,
    uniqueResponders: Number(stats?.uniqueResponders) || 0,
    totalResponses: Number(stats?.totalResponses) || 0,
    responseRate: clampPercent(stats?.responseRate),
    phoneCalls: Number(stats?.phoneCalls) || 0,
    qrScans: Number(stats?.qrScans) || 0,
    purlVisits: Number(stats?.purlVisits) || 0,
    lastRefreshedAt: engagementData?.lastRefreshedAt || null,
    responseChannels: (stats?.responseChannels || []).map(normalizeEngagementChannel),
    performanceByProduct: (stats?.performanceByProduct || []).map((product, index) => ({
      id: product.id ?? `${product.product}-${index}`,
      product: product.product || 'Unknown product',
      trackedDelivered: Number(product.trackedDelivered) || 0,
      responses: Number(product.responses) || 0,
      responseRate: clampPercent(product.responseRate),
      responseSource: product.responseSource || 'qr',
    })),
    trackingAssets: trackingRows.map(normalizeTrackingPerformanceRow),
    recentResponses: recentRows.map(normalizeRecentResponseRow),
    accuracy: stats?.accuracy || {},
    isDashboardCardsDisabled: Boolean(
      engagementData?.isDashboardCardsDisabled || stats?.isDashboardCardsDisabled
    ),
    isSampleData: Boolean(engagementData?.isSampleData || stats?.isSampleData),
  };
};
